
import { Component, Prop, Watch } from 'vue-property-decorator';
import ModalView from '@/components/helper/modal-view.vue';
import ListBase from '@/lib/listbase';
import { processExcelFile } from './utils/objectives-import';
import { applyCustomFilters } from '../../utils/sheet-utils';

interface SheetData {
  headers: string[];
  items: Array<Record<string, any>>;
}
const IMPORT_TYPE_MAP: Record<'internal' | 'external' | 'total', number> = {
  internal: 0,
  external: 1,
  total: 2
};

@Component({
  components: { ModalView }
})
export default class ImportChanges extends ListBase {
  @Prop({ type: Boolean, default: false }) value!: boolean;
  @Prop({ type: Array, default: () => [] }) monthHeaders!: string[];
  @Prop({ default: '' }) readonly marcaSeleccionada!: string;
  @Prop({ type: Array }) dealers!: string[];

  modalShow: boolean = false;
  file: File | null = null;
  importedSheet: { headers: string[]; items: any[] } = { headers: [], items: [] };
  importLoading: boolean = false;
  exportLoading: boolean = false;

  importedCurrentPage: number = 1;
  importedItemsPerPage: number = 5;
  importedItemsPerPageOptions: number[] = [5, 10, 15];

  importTypeSelect: 'total' | 'internal' | 'external' | null = null;

  modalSetting: any = {
    entity: "sale",
    width: "95%",
    showForm: false,
    titleFull: 'Importar Objetivos',
    icon: 'mdi-upload',
    saveText: 'Actualizar Objetivos',
    saveIcon: 'mdi-upload',
    disabledSave: true,
    persistent: false
  };

  get importTypeItems() {
    return [
      { text: 'Todo', value: 'total' },
      { text: 'Internos', value: 'internal' },
      { text: 'Externos', value: 'external' }
    ];
  }

  get disabled(): boolean {
    return this.file === null;
  }

  get hasImportedSheet(): boolean {
    return this.importedSheet && this.importedSheet.headers.length > 0;
  }

  generateHeaders(headerRow: string[]): any[] {
    return headerRow.map(col => ({
      text: col,
      value: col
    }));
  }

  get paginatedItems(): any[] {
    const start = (this.importedCurrentPage - 1) * this.importedItemsPerPage;
    return this.importedSheet.items.slice(start, start + this.importedItemsPerPage);
  }

  get importedPageStart(): number {
    return this.importedSheet.items.length
      ? (this.importedCurrentPage - 1) * this.importedItemsPerPage + 1
      : 0;
  }

  get importedPageEnd(): number {
    return Math.min(this.importedCurrentPage * this.importedItemsPerPage, this.importedSheet.items.length);
  }

  get importedTotalPages(): number {
    return Math.ceil(this.importedSheet.items.length / this.importedItemsPerPage);
  }

  @Watch('value', { immediate: true })
  onValueChanged(val: boolean) {
    this.modalShow = val;
    if (val) {
      this.initForm();
    }
  }

  @Watch('modalShow')
  onModalShowChanged(val: boolean) {
    if (!val) {
      this.$emit('input', false);
    }
  }

  private updateModalTitle(): void {
    switch (this.importTypeSelect) {
      case 'internal':
        this.modalSetting.titleFull = 'Importar Objetivos - Revoshop';
        break;
      case 'external':
        this.modalSetting.titleFull = 'Importar Objetivos - Dealers terceros';
        break;
      case 'total':
        this.modalSetting.titleFull = 'Importar Objetivos - Revoshop y Dealers terceros';
        break;
      default:
        this.modalSetting.titleFull = 'Importar Objetivos';
    }
  }

  initForm(): void {
    this.updateModalTitle();
    this.modalSetting.saveText = 'Actualizar Objetivos';
    this.reset();
  }

  @Watch('importTypeSelect')
  onImportTypeSelectChanged(val: 'internal' | 'external' | 'total'): void {
    this.updateModalTitle();
    const idx = IMPORT_TYPE_MAP[val];
    console.log('[ImportChanges] 🔄 importTypeSelect:', val, '→ emito índice', idx);
    this.$emit('update-import-type', idx);
  }

  reset(): void {
    this.file = null;
    this.importedSheet = { headers: [], items: [] };
    this.modalSetting.disabledSave = true;
    this.importedCurrentPage = 1;
    this.importedItemsPerPage = 5;
  }

  async importFile(): Promise<void> {
    if (this.file && !this.importTypeSelect) {
      this.$toast.info("Por favor selecciona dónde importar los datos");
      return;
    }

    if (!this.file) return;
    this.importLoading = true;
    try {
      if (this.importTypeSelect === 'total') {
        const data1 = await processExcelFile(this.file, "Proyectado Fabrica Revoshop", "local");
        const data2 = await processExcelFile(this.file, "Proyectado Dealers terceros", "local");
        if (data1 && data2) {
          const combinedHeaders = data1.headers;
          const combinedItems = [...data1.items, ...data2.items];
          let combinedSheet: SheetData = { headers: combinedHeaders, items: combinedItems };
          combinedSheet = this.applyCustomFilters(combinedSheet);
          this.importedSheet = combinedSheet;
          this.modalSetting.disabledSave = false;
        } else {
          this.$toast.error("No se encontraron datos válidos en ambas hojas");
        }
      } else if (this.importTypeSelect === 'internal') {
        const data = await processExcelFile(this.file, "Proyectado Fabrica Revoshop", "local");
        if (data) {
          const filtered = this.filterImportChanges(data);
          if (filtered.items.length > 0) {
            this.importedSheet = filtered;
            this.modalSetting.disabledSave = false;
          } else {
            this.$toast.error("No se encontraron datos numéricos válidos para importar.");
          }

        } else {
          this.$toast.error("No se encontraron datos válidos en la hoja de Proyectado Fabrica Revoshop");
        }
      } else if (this.importTypeSelect === 'external') {
        const data = await processExcelFile(this.file, "Proyectado Dealers terceros", "local");
        if (data) {
          const filtered = this.filterImportChanges(data);
          if (filtered.items.length > 0) {
            this.importedSheet = filtered;
            this.modalSetting.disabledSave = false;
          } else {
            this.$toast.error("No se encontraron datos numéricos válidos para importar.");
          }

        } else {
          this.$toast.error("No se encontraron datos válidos en la hoja de Proyectado Dealers terceros");
        }
      } else {
        this.$toast.error("Tipo de vista no válido");
      }
    } catch (err) {
      console.error("Error al procesar archivo:", err);
      this.$toast.error("Ocurrió un error procesando el archivo.");
    } finally {
      this.importLoading = false;
    }
  }

  applyCustomFilters(sheet: SheetData): SheetData {
    return applyCustomFilters(
      sheet,
      ["CONCESIONARIO", "MODELOS"],
      this.dealers,
      (msg) => this.$toast.info(msg)
    );
  }

  get importedSheetTitle(): string {
    if (this.importTypeSelect === 'internal') {
      return 'Datos - Revoshop';
    } else if (this.importTypeSelect === 'external') {
      return 'Datos - Dealers terceros';
    } else if (this.importTypeSelect === 'total') {
      return 'Datos - Revoshop y Dealers terceros';
    } else {
      return 'Datos';
    }
  }

  private parseCell(raw: any): number | null {
    const s = raw == null ? '' : String(raw).trim();
    if (s === '' || /^[-–—]$/.test(s)) return null;
    const n = Number(s.replace(',', '.'));
    return isNaN(n) ? null : n;
  }

  private filterImportChanges(sheet: SheetData): SheetData {
    const [dealerCol, itemCol, ...numCols] = sheet.headers;
    const outputItems: any[] = [];
    let currentDealer: Record<string, any> | null = null;
    let bufferRows: any[] = [];
    let validCount = 0;
    const usedCols = new Set<string>();

    const flushGroup = () => {
      if (!currentDealer) return;
      if (validCount > 0) {
        // console.info(`[ImportChanges] Dealer "${currentDealer[dealerCol]}" importado con ${validCount} valores válidos.`);
        outputItems.push({ ...currentDealer });
        bufferRows.forEach(r => outputItems.push(r));
      } else {
        // console.info(`[ImportChanges] Dealer "${currentDealer[dealerCol]}" omitido (0 datos válidos).`);
      }
      bufferRows = [];
      validCount = 0;
    };

    for (const row of sheet.items) {
      const isDealerRow = String(row[dealerCol] ?? '').trim() !== '';
      if (isDealerRow) {
        flushGroup();
        currentDealer = row;
      } else if (currentDealer) {
        const parsed: any = { [dealerCol]: '', [itemCol]: row[itemCol] };
        let rowHasData = false;

        for (const col of numCols) {
          const v = this.parseCell(row[col]);
          if (v !== null) {
            parsed[col] = v;
            validCount++;
            rowHasData = true;
            usedCols.add(col);
          } else {
            parsed[col] = null;
          }
        }

        if (rowHasData) {
          bufferRows.push(parsed);
        } else {
          // console.info(`[ImportChanges] Fila ignorada (sin datos) dealer="${currentDealer[dealerCol]}", ítem="${row[itemCol]}"`);
        }
      }
    }
    flushGroup();

    const validNumCols = numCols.filter(col => usedCols.has(col));

    const finalHeaders = [dealerCol, itemCol, ...validNumCols];

    const finalItems = outputItems.map(row => {
      const pruned: any = {};
      for (const h of finalHeaders) {
        pruned[h] = row[h] ?? null;
      }
      return pruned;
    });

    return {
      headers: finalHeaders,
      items: finalItems
    };
  }

  async save(): Promise<any> {
    try {
      this.$emit('update-objectives', {
        type: this.importTypeSelect,
        sheet: this.importedSheet
      });
      this.$toast.success("Objetivos actualizados correctamente");
      this.$emit('save-success');
      this.cancel();
      return true;
    } catch (error) {
      console.error("Error al actualizar objetivos:", error);
      this.$toast.error("Error actualizando objetivos");
      return false;
    }
  }

  cancel(): void {
    this.reset();
    this.modalShow = false;
    this.$emit('input', false);
  }

  async exportToExcel() {
    const selectedBrand = this.marcaSeleccionada;
    if (!selectedBrand || !selectedBrand.trim()) {
      this.$toast.error("Seleccione una marca");
      return;
    }
    this.exportLoading = true;
    try {
      await this.$store.dispatch('transaction/downloadReport', selectedBrand.trim());
    } catch (error) {
      console.error("Error al descargar el reporte:", error);
    } finally {
      this.exportLoading = false;
    }
  }
}
